import styled, { css } from 'styled-components';
import { Icon } from '@iconify/react';

type ButtonProps = {
  full?: boolean;
  size: 'xsm' | 'sm' | 'md' | 'lg';
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'light' | 'dark' | 'coopercred' | 'dev';
  outline?: boolean;
}

export type IconProps = {
  $iconPosition?: 'left' | 'right',
  $hasChildren?: boolean
}

const paddingMap = {
  xsm: { padding: '5px' },
  sm: { padding: '8px' },
  md: { padding: '10px' },
  lg: { padding: '15px' }
};

export const Button = styled.button<ButtonProps>`
  width: ${({ full }) => (full ? '100%' : 'fit-content')};
  
  border-radius: 6px;
  border: 2px solid transparent;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;

  display: flex;
  align-items: center;
  justify-content: ${({ full }) => (full ? 'center' : 'space-between')};

  transition: all 0.2s ease-in-out;

  ${({ size }) =>
    size &&
    css`
    padding: ${paddingMap[size].padding};
  `}

  /* Primary Variant */
  ${({ variant, outline, theme }) =>
    variant === 'primary' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: ${theme.colors.primary};
        border-color: ${theme.colors.primary};
        &:hover {
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
        }
      ` : css`
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};
        border-color: ${theme.colors.primary};
        &:hover {
          background-color: ${theme.colors.primary_hover};
          border-color: ${theme.colors.primary_hover};
        }
      `}
    `}

  /* Secondary Variant */
  ${({ variant, outline, theme }) =>
    variant === 'secondary' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: ${theme.colors.text};
        border-color: ${theme.colors.border_grey};
        &:hover {
          background-color: ${theme.colors.border_grey};
        }
      ` : css`
        background-color: ${theme.colors.border_grey};
        color: ${theme.colors.text};
        border-color: ${theme.colors.border_grey};
        &:hover {
          background-color: #e2e6ea;
        }
      `}
    `}

  /* Success Variant */
  ${({ variant, outline }) =>
    variant === 'success' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #28a745;
        border-color: #28a745;
        &:hover {
          background-color: #28a745;
          color: white;
        }
      ` : css`
        background-color: #28a745;
        color: white;
        border-color: #28a745;
        &:hover {
          background-color: #218838;
          border-color: #1e7e34;
        }
      `}
    `}

  /* Warning Variant */
  ${({ variant, outline }) =>
    variant === 'warning' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #ffc107;
        border-color: #ffc107;
        &:hover {
          background-color: #ffc107;
          color: #212529;
        }
      ` : css`
        background-color: #ffc107;
        color: #212529;
        border-color: #ffc107;
        &:hover {
          background-color: #e0a800;
          border-color: #d39e00;
        }
      `}
    `}

  /* Danger Variant */
  ${({ variant, outline }) =>
    variant === 'danger' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #dc3545;
        border-color: #dc3545;
        &:hover {
          background-color: #dc3545;
          color: white;
        }
      ` : css`
        background-color: #dc3545;
        color: white;
        border-color: #dc3545;
        &:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }
      `}
    `}

  /* Info Variant */
  ${({ variant, outline }) =>
    variant === 'info' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #17a2b8;
        border-color: #17a2b8;
        &:hover {
          background-color: #17a2b8;
          color: white;
        }
      ` : css`
        background-color: #17a2b8;
        color: white;
        border-color: #17a2b8;
        &:hover {
          background-color: #138496;
          border-color: #117a8b;
        }
      `}
    `}

  /* Light Variant */
  ${({ variant, outline }) =>
    variant === 'light' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #6c757d;
        border-color: #f8f9fa;
        &:hover {
          background-color: #f8f9fa;
          color: #6c757d;
        }
      ` : css`
        background-color: #f8f9fa;
        color: #6c757d;
        border-color: #f8f9fa;
        &:hover {
          background-color: #e2e6ea;
          border-color: #dae0e5;
        }
      `}
    `}

  /* Dark Variant */
  ${({ variant, outline }) =>
    variant === 'dark' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #343a40;
        border-color: #343a40;
        &:hover {
          background-color: #343a40;
          color: white;
        }
      ` : css`
        background-color: #343a40;
        color: white;
        border-color: #343a40;
        &:hover {
          background-color: #23272b;
          border-color: #1d2124;
        }
      `}
    `}

  /* Coopercred Variant (mantido para compatibilidade) */
  ${({ variant, outline }) =>
    variant === 'coopercred' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: #039639;
        border-color: #039639;
        &:hover {
          background-color: #039639;
          color: white;
        }
      ` : css`
        background-color: #039639;
        color: white;
        border-color: #039639;
        &:hover {
          background-color: #026b29;
          border-color: #026b29;
        }
      `}
    `}

  /* Dev Variant (mantido para compatibilidade) */
  ${({ variant, outline, theme }) =>
    variant === 'dev' &&
    css`
      ${outline ? css`
        background-color: transparent;
        color: ${theme.colors.dev};
        border-color: ${theme.colors.dev};
        &:hover {
          background-color: ${theme.colors.dev};
          color: ${theme.title};
        }
      ` : css`
        background-color: ${theme.colors.dev};
        color: ${theme.title};
        border-color: ${theme.colors.dev};
        &:hover {
          background-color: ${theme.colors.warning};
          border-color: ${theme.colors.warning};
        }
      `}
    `}

  padding-inline: 20px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    background-color: #D9D9D9 !important;
    color: ${props => props.theme.colors.grey_text} !important;
    border-color: #D9D9D9 !important;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;


export const ButtonIcon = styled(Icon) <IconProps>`
  ${({ $iconPosition, $hasChildren }) =>
    $hasChildren == true && (
      $iconPosition === 'left' ?
        css`
      margin-right: 5px;
    `:
        css`
      margin-left: 5px;
    `)
  }
`;
